/** Pure noise functions — seed-parameterised, no side effects. */

import { lerp, smooth } from '../utils/math-helpers.js';

export function hash2i(x, z, seed, worldSeed) {
  const mixedSeed = ((seed | 0) ^ worldSeed) | 0;
  let h = ((x | 0) * 374761393) ^ ((z | 0) * 668265263) ^ (mixedSeed * 1597334677);
  h = (h ^ (h >>> 13)) * 1274126177;
  return ((h ^ (h >>> 16)) >>> 0) / 4294967295;
}

export function hash3i(x, y, z, seed, worldSeed) {
  const mixedSeed = ((seed | 0) ^ worldSeed) | 0;
  let h = ((x | 0) * 374761393) ^ ((y | 0) * 1274126177) ^ ((z | 0) * 668265263) ^ (mixedSeed * 1597334677);
  h = (h ^ (h >>> 13)) * 2246822519;
  return ((h ^ (h >>> 16)) >>> 0) / 4294967295;
}

export function valueNoise2(x, z, freq, seed, worldSeed) {
  const px = x * freq;
  const pz = z * freq;
  const x0 = Math.floor(px);
  const z0 = Math.floor(pz);
  const x1 = x0 + 1;
  const z1 = z0 + 1;
  const tx = smooth(px - x0);
  const tz = smooth(pz - z0);

  const n00 = hash2i(x0, z0, seed, worldSeed) * 2 - 1;
  const n10 = hash2i(x1, z0, seed, worldSeed) * 2 - 1;
  const n01 = hash2i(x0, z1, seed, worldSeed) * 2 - 1;
  const n11 = hash2i(x1, z1, seed, worldSeed) * 2 - 1;
  return lerp(lerp(n00, n10, tx), lerp(n01, n11, tx), tz);
}

export function valueNoise3(x, y, z, freq, seed, worldSeed) {
  const px = x * freq;
  const py = y * freq;
  const pz = z * freq;

  const x0 = Math.floor(px);
  const y0 = Math.floor(py);
  const z0 = Math.floor(pz);
  const x1 = x0 + 1;
  const y1 = y0 + 1;
  const z1 = z0 + 1;

  const tx = smooth(px - x0);
  const ty = smooth(py - y0);
  const tz = smooth(pz - z0);

  const n000 = hash3i(x0, y0, z0, seed, worldSeed) * 2 - 1;
  const n100 = hash3i(x1, y0, z0, seed, worldSeed) * 2 - 1;
  const n010 = hash3i(x0, y1, z0, seed, worldSeed) * 2 - 1;
  const n110 = hash3i(x1, y1, z0, seed, worldSeed) * 2 - 1;
  const n001 = hash3i(x0, y0, z1, seed, worldSeed) * 2 - 1;
  const n101 = hash3i(x1, y0, z1, seed, worldSeed) * 2 - 1;
  const n011 = hash3i(x0, y1, z1, seed, worldSeed) * 2 - 1;
  const n111 = hash3i(x1, y1, z1, seed, worldSeed) * 2 - 1;

  const nx00 = lerp(n000, n100, tx);
  const nx10 = lerp(n010, n110, tx);
  const nx01 = lerp(n001, n101, tx);
  const nx11 = lerp(n011, n111, tx);

  const nxy0 = lerp(nx00, nx10, ty);
  const nxy1 = lerp(nx01, nx11, ty);
  return lerp(nxy0, nxy1, tz);
}

export function fbm2(x, z, opts, worldSeed) {
  const {
    octaves = 4,
    freq = 0.01,
    gain = 0.5,
    lacunarity = 2.0,
    seed = 0
  } = opts;
  let amp = 1;
  let sum = 0;
  let norm = 0;
  let f = freq;
  for (let i = 0; i < octaves; i++) {
    sum += valueNoise2(x, z, f, seed + i * 101, worldSeed) * amp;
    norm += amp;
    amp *= gain;
    f *= lacunarity;
  }
  return sum / (norm || 1);
}
